import React, { useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { Alert, Button, Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { askAssistant, assistantConfig } from './assistantClient';
import Markdown from './Markdown';

export default function AssistantChat({ active = true }) {
  const { instance, accounts } = useMsal();
  const url = useSelector(s => s.apiUrls.urls.assistant);
  const config = assistantConfig();
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [activity, setActivity] = useState(null);
  const [error, setError] = useState('');
  const current = useRef(null);
  const history = useRef([]);
  const conversation = useRef(null);
  const followLatest = useRef(true);
  const composer = useRef(null);
  useEffect(() => {
    if (followLatest.current && conversation.current) {
      conversation.current.scrollTop = conversation.current.scrollHeight;
    }
  }, [messages, activity, error]);
  const stop = () => {
    current.current?.abort();
    current.current = null;
    setBusy(false);
    setActivity(null);
  };
  useEffect(() => () => { current.current?.abort(); current.current = null; }, []);
  useEffect(() => {
    if (!active) {
      current.current?.abort(); current.current = null; setBusy(false); setActivity(null);
    }
  }, [active]);
  const send = async e => {
    e.preventDefault();
    const message = draft.trim();
    if (!message || current.current || !config.enabled || !url) return;
    const controller = new AbortController();
    followLatest.current = true;
    current.current = controller;
    const previous = history.current;
    const user = { role: 'user', content: message };
    let answer = '';
    setMessages([...previous, user, { role: 'assistant', content: '' }]);
    setDraft(''); setError(''); setBusy(true); setActivity('Thinking');
    const active = () => current.current === controller;
    const timeout = setTimeout(() => {
      if (active()) { stop(); setError('The assistant timed out. Please try a narrower question.'); }
    }, 180000);
    try {
      await askAssistant({ url, config, message, history: previous, signal: controller.signal,
        getToken: async scope => {
          const account = instance.getActiveAccount() || accounts[0];
          if (!account) throw new Error('Sign in again to use the assistant.');
          try { return (await instance.acquireTokenSilent({ account, scopes: [scope] })).accessToken; }
          catch { throw new Error('Could not acquire an assistant API token. Check the configured scope or sign in again.'); }
        },
        onDelta: text => {
          if (!active()) return;
          answer += text;
          setMessages([...previous, user, { role: 'assistant', content: answer }]);
        },
        onActivity: text => { if (active()) setActivity(text); },
      });
      if (active()) history.current = [...previous, user, { role: 'assistant', content: answer }].slice(-30);
    } catch (err) {
      if (active() && err.name !== 'AbortError') setError(err.message);
    } finally {
      clearTimeout(timeout);
      if (active()) { current.current = null; setBusy(false); setActivity(null); }
    }
  };
  return <section aria-label="AI assistant" className="portal-assistant-chat">
    <div className="portal-assistant-transcript" ref={conversation} onScroll={e => {
      const el = e.currentTarget;
      followLatest.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    }}>
    {!messages.length && <div className="portal-assistant-welcome">
      <p className="portal-assistant-day">Today</p>
      <div className="portal-assistant-greeting"><span className="portal-assistant-emblem" aria-hidden="true">✦</span><p>Hi! How can I help you today?</p></div>
      <div className="portal-assistant-suggestions">
        {[
          ['Shipping', 'When does job 123456 ship?'],
          ['Missing details', 'Does job 123456 have any TBDs?'],
          ['Change orders', 'Does job 123456 have outstanding change order requests?'],
        ].map(([label, question]) => <button type="button" key={label} onClick={() => {
          setDraft(question); composer.current?.focus();
        }} disabled={!config.enabled || !url}>
          {label}
        </button>)}
      </div>
      <p className="portal-assistant-caption">Choose a topic, then replace the example job number with yours.</p>
    </div>}
    {!config.enabled && <Alert variant="info">AI chat is not configured yet. Field search is still available.</Alert>}
    {config.enabled && !url && <Alert variant="warning">The assistant endpoint is not configured.</Alert>}
    <div aria-live="polite" aria-busy={busy}>
      {messages.map((m, i) => <div key={i} className={`portal-assistant-message portal-assistant-message--${m.role}`}>
        <span className="portal-assistant-speaker">{m.role === 'user' ? 'You' : '✦ Portal assistant'}</span><Markdown text={m.content} />
      </div>)}
      {activity && <p role="status" className="portal-assistant-activity">{activity}…</p>}
    </div>
    {error && <Alert variant="danger">{error}</Alert>}
    </div>
    <Form onSubmit={send} className="portal-assistant-composer">
      <div className="portal-assistant-input-row"><Form.Group controlId="assistant-question">
        <Form.Label className="visually-hidden">Ask AI</Form.Label>
        <Form.Control ref={composer} as="textarea" rows={2} maxLength={4000} value={draft}
          onChange={e => setDraft(e.target.value)} disabled={busy || !config.enabled || !url}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(e); }
          }}
          placeholder="Type a message…" />
      </Form.Group>
      <Button className="portal-assistant-send" variant="link" type="submit" aria-label="Send" disabled={busy || !draft.trim() || !config.enabled || !url}>
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="M3 3l19 9-19 9 4-9-4-9zM7 12h15" /></svg>
      </Button></div>
      <div className="d-flex gap-2 mt-2">
        {busy && <Button variant="outline-secondary" onClick={stop}>Stop</Button>}
        <Button variant="outline-secondary" disabled={!messages.length} onClick={() => {
          stop(); history.current = []; setMessages([]); setError('');
        }}>Clear conversation</Button>
      </div>
      <p className="portal-assistant-caption">AI assistant · Enter to send · Shift+Enter for a new line</p>
    </Form>
  </section>;
}
